import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { isEmpty, isEqual } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";
import { useMemberPaymentApi } from "@apis/useMemberPaymentApi";

/**
 * 管理付款常用信用卡的 Hook
 * 整合了表單資料管理（useFormDataWithKey）和套用常用資料功能
 *
 * @param {number} memberID - 會員 ID
 * @param {Object} [initialSource] - 初始資料來源（可選，用於從 cycleOrder 初始化）
 * @returns {Object} 包含 formData 和 applyDefaultCreditCard 函數
 */
const useDefaultCreditCard = (memberID, initialSource = {}) => {
	const setMessageDialog = useMessageDialog();
	const { getMemberDefaultCreditCardApi } = useMemberPaymentApi();

	// 表單資料管理（整合 useFormDataWithKey）
	const [formData, setFormData] = useState({
		source: initialSource,
		key: uuid(),
	});

	// 快取已請求的資料
	const cachedDataRef = useRef(null);
	// 標記是否正在請求中
	const isLoadingRef = useRef(false);

	// 當 memberID 改變時，清空快取（避免使用前一個會員的資料）
	useEffect(() => {
		if (cachedDataRef.current) {
			// 重置快取資料
			cachedDataRef.current = null;

			// 重置表單資料
			setFormData({
				source: {},
				key: uuid(),
			});
		}
		// eslint-disable-next-line
	}, [memberID]);

	// 當 initialSource 變化時，更新 formData（用於從 cycleOrder 初始化）
	useEffect(() => {
		if (!isEmpty(initialSource)) {
			setFormData({
				source: initialSource,
				key: uuid(),
			});
		}
		// eslint-disable-next-line
	}, [initialSource]);

	/**
	 * 套用會員常用信用卡資料
	 */
	const applyDefaultCreditCard = useCallback(
		async () => {
			// 檢查 memberID 是否存在且有效
			if (!memberID || memberID <= 0) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "請先選擇會員，謝謝。",
				});
				return;
			}

		// 如果正在請求中，不重複請求
		if (isLoadingRef.current) {
			return;
		}

		// 如果已經有快取的資料，直接套用
		if (cachedDataRef.current !== null) {
			const data = cachedDataRef.current;
			// 驗證 cardRelation
			if (!isEqual(data?.cardRelation, "本人")) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "很抱歉！無法取得相關資料，可能是沒有設定常用信用卡或不是信用卡持卡人，謝謝。",
				});
				return;
			}

			// 更新表單資料
			setFormData({
				source: data,
				key: uuid(),
			});
			return;
		}

		// 開始請求
		isLoadingRef.current = true;

			try {
				const resp = await getMemberDefaultCreditCardApi(memberID);

				if (!resp || isEmpty(resp)) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，可能是沒有設定常用信用卡或不是信用卡持卡人，謝謝。",
					});
					isLoadingRef.current = false;
					return;
				}

				// 驗證 cardRelation 必須為「本人」
				if (!isEqual(resp?.cardRelation, "本人")) {
					setMessageDialog({
						open: true,
						title: "提醒",
						msg: "很抱歉！無法取得相關資料，可能是沒有設定常用信用卡或不是信用卡持卡人，謝謝。",
					});
					isLoadingRef.current = false;
					return;
				}

			// 存入快取
			cachedDataRef.current = resp;

			// 更新表單資料
			setFormData({
				source: resp,
				key: uuid(),
			});
			} catch (error) {
				setMessageDialog({
					open: true,
					title: "提醒",
					msg: "很抱歉！無法取得相關資料，可能是沒有設定常用信用卡或不是信用卡持卡人，謝謝。",
				});
			} finally {
				isLoadingRef.current = false;
			}
		},
		// eslint-disable-next-line
		[memberID, getMemberDefaultCreditCardApi, setMessageDialog]
	);

	return {
		formData, // { source, key }
		applyDefaultCreditCard,
	};
};

export default useDefaultCreditCard;
